/**
 * Default Contract Template Seed Data
 *
 * Extracted from: LW Loan Officer Compensation Agreement.pdf
 * Contains all 18 sections with legal language and dynamic variables
 */

import type { ContractTemplate } from './contract-types';
import { generateId } from './contract-utils';

/**
 * Create the default contract template
 */
export function createDefaultContractTemplate(): ContractTemplate {
  const now = new Date().toISOString();

  return {
    id: 'default_template_v1',
    name: 'Loan Officer Compensation Agreement',
    version: 2,
    createdAt: now,
    updatedAt: now,
    requiredVariables: [
      'firstName',
      'lastName',
      'fullName',
      'email',
      'phone',
      'fullMailingAddress',
      'nmlsNumber',
      'dreNumber',
      'bankedCompensation',
      'brokerCommissionSplit',
      'position',
      'startDate',
      'contractDate',
      'companyName'
    ],
    sections: [
      // ====================================================================
      // SECTION 1: LOAN OFFICER INFORMATION
      // ====================================================================
      {
        id: generateId('section'),
        type: 'loan_officer_info',
        title: 'Loan Officer Information',
        order: 'title',
        isEditable: true,
        hasDynamicData: true,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: 'LOAN OFFICER COMPENSATION AGREEMENT',
            level: 1
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'This Loan Officer Compensation Agreement ("Agreement") is entered into on {{contractDate:date}} ("Agreement Date") by and between {{companyName}} ("Company") and {{fullName}} ("Loan Officer"), with employment to commence on the date Regulator has authorized Loan Originator to conduct business with our organization via the NMLS.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Loan Officer Information',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'table',
            rows: [
              ['Legal Name:', '{{fullName}}'],
              ['Preferred First Name:', '{{preferredFirstName}}'],
              ['Email Address:', '{{email}}'],
              ['Phone Number:', '{{phone:phone}}'],
              ['Mailing Address:', '{{fullMailingAddress}}'],
              ['NMLS Number:', '{{nmlsNumber}}'],
              ['DRE Number:', '{{dreNumber}}'],
              ['Position:', '{{position}}']
            ]
          }
        ]
      },

      // ====================================================================
      // SECTION 2: EMPLOYMENT CLASSIFICATION
      // ====================================================================
      {
        id: generateId('section'),
        type: 'employment_classification',
        title: 'Employment Classification',
        order: 1,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '1. Employment Classification',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer is classified as an at-will employee of the Company. This classification is subject to review and may be modified based on operational needs, regulatory requirements, or changes in the Loan Officer\'s role and responsibilities.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'As an employee, the Loan Officer will receive compensation as outlined in this Agreement and may be eligible for certain benefits as determined by Company policy. The Loan Officer acknowledges that this employment relationship may be terminated by either party at any time, with or without cause, and with or without notice, subject to applicable law.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Your employment classification for tax purposes:',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'W-2 Employee or 1099 Independent Contractor (determined by state regulations). Applicable state and federal regulations determine employment classification (W-2 vs 1099). Your classification will be confirmed according to your work location and relevant laws. Tax withholdings and reporting will be handled accordingly.'
          }
        ]
      },

      // ====================================================================
      // SECTION 3: COMPENSATION STRUCTURE
      // ====================================================================
      {
        id: generateId('section'),
        type: 'compensation_structure',
        title: 'Compensation Structure',
        order: 2,
        isEditable: true,
        hasDynamicData: true,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '2. Compensation Structure',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer\'s compensation shall be based on the following structure:'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'A. Correspondent (Banked) Loans',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'For loans originated and closed through the Company\'s correspondent lending channel (banked loans), the Loan Officer shall receive compensation of {{bankedCompensation:percentage}} of the loan amount, subject to a maximum cap of {{maximumCompensation:currency}} per loan.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Example calculation: For a $500,000 loan at {{bankedCompensation:percentage}}, the gross compensation would be calculated as $500,000 × {{bankedCompensation:percentage}} = {{bankedCompensationExample:currency}} (subject to the {{maximumCompensation:currency}} maximum cap).'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'B. Broker Loans',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'For loans brokered to third-party lenders, the Loan Officer shall receive {{brokerCommissionSplit:percentage}} of the total broker commission earned by the Company on such transactions, calculated after the Company Origination Fee deduction as described below.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Company Origination Fee',
            level: 4
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'For each brokered loan transaction, a Company Origination Fee of Seven Hundred Fifty Dollars ($750.00) shall be deducted from the gross broker commission before the Loan Officer\'s commission split is calculated. This fee covers Company overhead, compliance, and operational costs associated with brokered transactions.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Example: If the Company receives a $10,000 gross broker commission, the $750 Company Origination Fee is first deducted, leaving $9,250. The Loan Officer\'s commission is then calculated as {{brokerCommissionSplit:percentage}} of $9,250.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The broker commission split applies to the net commission received by the Company after deduction of the Company Origination Fee, any third-party fees, processing costs, or wholesale lender fees.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Errors & Omissions (E&O) Insurance Premium',
            level: 4
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Company maintains an Errors & Omissions (E&O) insurance policy covering all loan origination activities. To fund this coverage, a premium of Fifty Dollars ($50.00) per funded loan shall be deducted from the Loan Officer\'s commission on each transaction. This deduction applies to both correspondent (banked) and brokered loan transactions.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The E&O insurance premium deduction ensures continuous professional liability coverage for the Loan Officer\'s origination activities while employed by the Company. This coverage protects both the Loan Officer and the Company from claims arising from errors, omissions, or negligence in the loan origination process.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Company reserves the right to modify compensation structures with 30 days\' written notice to accommodate changes in market conditions, regulatory requirements, or business operations.'
          },

          // Mentorship Program (conditional - only shown if mentor assigned)
          {
            id: generateId('content'),
            type: 'heading',
            text: 'C. Mentorship Program',
            level: 3,
            conditionalDisplay: {
              field: 'mentorName',
              operator: 'isNotEmpty'
            }
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Lendwise will provide mentorship support through the Company\'s mentorship program. {{mentorName}} will serve as your assigned mentor for your first five (5) brokered loan transactions.',
            conditionalDisplay: {
              field: 'mentorName',
              operator: 'isNotEmpty'
            }
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Phase 1: Training Period (First 5 Brokered Deals)',
            level: 4,
            conditionalDisplay: {
              field: 'mentorName',
              operator: 'isNotEmpty'
            }
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'During the mentorship period, the Loan Officer\'s broker commission split ({{brokerCommissionSplit:percentage}} as outlined in Section 2B above) will be allocated between the Loan Officer and their assigned mentor as follows:',
            conditionalDisplay: {
              field: 'mentorName',
              operator: 'isNotEmpty'
            }
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'Loan Officer receives: {{loanOfficerMentorSplit:percentage}} of their allocated broker commission split',
              'Mentor ({{mentorName}}) receives: {{brokerMentorCommissionSplit:percentage}} of the Loan Officer\'s allocated broker commission split'
            ],
            conditionalDisplay: {
              field: 'mentorName',
              operator: 'isNotEmpty'
            }
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Example: If the Company earns a $10,000 broker commission on a transaction, and your broker split is {{brokerCommissionSplit:percentage}} ({{mentorshipLoAllocation:currency}}), then {{mentorshipMentorReceives:currency}} will be allocated to {{mentorName}} as the mentor, and you will receive {{mentorshipLoReceives:currency}}. The Company retains {{mentorshipCompanyRetains:currency}}.',
            conditionalDisplay: {
              field: 'mentorName',
              operator: 'isNotEmpty'
            }
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Phase 2: Standard Compensation (After 5 Brokered Deals)',
            level: 4,
            conditionalDisplay: {
              field: 'mentorName',
              operator: 'isNotEmpty'
            }
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Upon successful completion of five (5) brokered loan transactions under the mentorship program, you will automatically transition to standard compensation structure as outlined in Section 2B (Broker Loans) above, receiving 100% of your agreed-upon broker commission split without mentor allocation.',
            conditionalDisplay: {
              field: 'mentorName',
              operator: 'isNotEmpty'
            }
          }
        ]
      },

      // ====================================================================
      // SECTION 4: PAYMENT TERMS
      // ====================================================================
      {
        id: generateId('section'),
        type: 'payment_terms',
        title: 'Payment Terms',
        order: 3,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '3. Payment Terms',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Commission Payment Timing',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Commission payments shall be made on a semi-monthly basis, with payments issued on the 15th and the last business day of each month. Commissions are paid in the following pay period after the loan funds:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'Loans funding between the 1st and 15th of the month will be paid at the end of that same month.',
              'Loans funding between the 16th and the last day of the month will be paid on the 15th of the following month.'
            ]
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'As an illustration, if a loan funds on November 6, 2025, the commission for that loan will be disbursed on November 30, 2025 (or the preceding business day if the last day falls on a weekend or holiday). If a loan funds on November 20, 2025, the commission will be disbursed on December 15, 2025.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Method of Payment',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Payments shall be issued by check and mailed to the Loan Officer\'s designated mailing address on record. The Company shall not be responsible for delays resulting from incorrect or outdated address information provided by the Loan Officer.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Minimum Payment Threshold',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'A minimum payment threshold of one hundred dollars ($100) applies. Commission amounts totaling less than $100 for a given pay period shall accrue and be carried forward to the next scheduled payment period until the minimum threshold is met.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Commission Advances & Contingencies',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'All commission payments are considered advances and are contingent upon the final purchase of the loan by the investor.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer acknowledges and agrees that, in the event a loan is not purchased by the investor, the Company shall have the right to recover advanced commissions associated with such loan.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Right of Offset & Repayment Obligation',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Company may offset and deduct any advanced commissions owed from future commissions payable to the Loan Officer. If insufficient future commissions exist to satisfy the balance owed, the Loan Officer agrees to repay any outstanding balance within ten (10) business days of written notice from the Company.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Company reserves all rights and remedies available at law or equity to collect any unpaid balance.'
          }
        ]
      },

      // ====================================================================
      // SECTION 5: RECAPTURE/CLAWBACK
      // ====================================================================
      {
        id: generateId('section'),
        type: 'recapture_clawback',
        title: 'Recapture and Clawback Provisions',
        order: 4,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '4. Recapture and Clawback Provisions',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer acknowledges and agrees that commissions paid by Lendwise Mortgage ("Company") are subject to full recapture under the circumstances outlined below. The Company reserves the right to recover and/or offset all such amounts from future commissions or require repayment.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Triggering Events for Recapture',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Commissions paid to the Loan Officer shall be fully subject to recapture if any of the following occur:'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'a. Early Payoff / Early Refinance (EPO)',
            level: 4
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The loan is paid in full, refinanced, or otherwise satisfies within an investor-specified Early Payoff (EPO) period.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'b. Loan Repurchase',
            level: 4
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Company is required to repurchase the loan — for any reason — including, but not limited to, investor-identified defects, documentation issues, compliance findings, borrower misrepresentation, or loan performance deficiencies.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'c. Early Payment Default (EPD)',
            level: 4
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The borrower fails to make the first three (3) to six (6) required payments (as determined by investor guidelines), or otherwise triggers an Early Payment Default provision with the investor.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Recapture Amount',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The full commission amount paid to the Loan Officer for the affected loan shall be subject to recapture.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Repayment Obligation',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Recaptured amounts may be deducted from future commissions. If sufficient future commissions do not exist, the Loan Officer shall remit repayment to the Company within ten (10) business days of notice.'
          }
        ]
      },

      // ====================================================================
      // SECTION 6: COMPLIANCE & LICENSING
      // ====================================================================
      {
        id: generateId('section'),
        type: 'compliance_licensing',
        title: 'Compliance and Licensing Requirements',
        order: 5,
        isEditable: true,
        hasDynamicData: true,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '5. Compliance and Licensing Requirements',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer agrees to maintain all required licenses, registrations, and certifications necessary to perform loan origination activities in all applicable jurisdictions. Specifically, the Loan Officer must:'
          },
          {
            id: generateId('content'),
            type: 'numbered_list',
            items: [
              'Maintain an active NMLS registration (Current NMLS #: {{nmlsNumber}})',
              'Complete all continuing education requirements as mandated by federal and state regulations',
              'Immediately notify the Company of any license suspensions, revocations, or disciplinary actions',
              'Comply with all federal and state lending laws, including TILA, RESPA, ECOA, SAFE Act, and California Finance Lenders Law',
              'Adhere to all Company policies, procedures, and quality control standards',
              'Participate in required compliance training and testing programs',
              'Accurately complete all loan documentation and disclosures in accordance with regulatory requirements'
            ]
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Company will sponsor the Loan Officer\'s NMLS registration and state licenses as required. The Loan Officer is responsible for all license renewal fees, continuing education costs, and background check expenses unless otherwise agreed in writing.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Failure to maintain required licenses or comply with applicable regulations may result in immediate suspension of loan origination activities and potential termination of employment.'
          }
        ]
      },

      // ====================================================================
      // SECTION 7: AT-WILL EMPLOYMENT
      // ====================================================================
      {
        id: generateId('section'),
        type: 'at_will_employment',
        title: 'At-Will Employment',
        order: 6,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '6. At-Will Employment',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'At-Will Engagement',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'This Agreement establishes an at-will relationship between the Loan Officer and Lendwise Mortgage ("Company"). Either party may terminate this Agreement at any time, with or without cause, and without penalty, by providing written notice to the other party.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Immediate Termination for Cause',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Company maintains a zero-tolerance policy for fraud, regulatory violations, misrepresentation, licensing suspension or expiration, or any material breach of this Agreement. Any such violation shall result in immediate termination at the sole discretion of the Company.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Pipeline & Compensation Upon Termination',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Closed Loan Compensation Window',
            level: 4
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Upon termination, the Loan Officer shall remain eligible to receive commissions only for loans that:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'Close and fund within thirty (30) calendar days following the termination date;',
              'Are in good standing, and',
              'Have been fully and properly transferred to the Company\'s team with all documentation and borrower communications up to date.'
            ]
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Forfeiture of Compensation Outside Window',
            level: 4
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Any loan that does not close and fund within the thirty (30)-day period following termination will revert fully to the Company, and no commission shall be owed to the departing Loan Officer.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer acknowledges that loans are Company assets, and post-termination borrower communication is prohibited unless expressly authorized in writing by the Company.'
          }
        ]
      },

      // ====================================================================
      // SECTION 8: PERFORMANCE EXPECTATIONS
      // ====================================================================
      {
        id: generateId('section'),
        type: 'performance_expectations',
        title: 'Performance, Quality, and Compliance Standards',
        order: 7,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '7. Performance, Quality, and Compliance Standards',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Loan Quality Standards',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer shall maintain strict loan quality standards, including but not limited to the completion of accurate loan applications, timely and compliant disclosures, proper documentation collection, and full file integrity.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Failure to maintain acceptable loan quality, including repeated errors, omissions, or deficiencies, may result in corrective action, compensation adjustment, suspension of file assignment, or termination at the Company\'s discretion.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Customer Service Standards',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer is expected to provide professional and responsive customer service to borrowers, referral partners, and internal staff. This includes timely responses to borrower inquiries within twenty-four (24) hours, proactive communication throughout the loan process, and maintaining a courteous and professional tone in all interactions.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Failure to meet service requirements may result in corrective action or termination.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Regulatory and Policy Compliance',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer must comply with all applicable federal, state, and local mortgage regulations, including but not limited to CFPB guidelines, TILA-RESPA, ECOA, Fair Housing, HMDA, SAFE Act requirements, NMLS rules, and any Company policies and investor requirements.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Violations, suspected violations, or conduct that jeopardizes compliance or licensure shall result in immediate suspension or termination and may trigger legal or financial remedies.'
          }
        ]
      },

      // ====================================================================
      // SECTION 9: EXPENSES & FEES
      // ====================================================================
      {
        id: generateId('section'),
        type: 'expenses_fees',
        title: 'Expenses and Fees',
        order: 8,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '8. Expenses and Fees',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Loan Officer Expenses',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer is solely responsible for all expenses associated with maintaining their professional credentials and conducting business, including but not limited to:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'NMLS and state licensing (e.g., DRE where applicable)',
              'Continuing education requirements',
              'Personal marketing, branding, and advertising expenses',
              'Professional dues, membership fees, and communication tools'
            ]
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Note: Errors & Omissions (E&O) insurance coverage is provided by the Company. The cost of this coverage is funded through a $50 per-loan premium deduction as outlined in Section 2 (Compensation Structure).'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Failure to maintain active and compliant licensing or required insurance may result in suspension of loan origination privileges and/or termination.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Company-Provided Resources',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Lendwise Mortgage ("Company") will provide the Loan Officer with access to the Company\'s approved systems and services, which may include:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'CRM and lead management platforms',
              'Loan origination system (LOS)',
              'Processing, underwriting, and compliance review',
              'Closing and funding coordination',
              'Company-approved document software and communication tools'
            ]
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'These services are provided at no direct cost to the Loan Officer, unless otherwise specified in writing. The Company reserves the right to modify, suspend, or revoke system access at its discretion, including upon termination.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Marketing Co-Op Program (If Offered)',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Company may, at its sole discretion, offer marketing co-op funds or reimbursement programs. All co-op reimbursement requests must:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'Be pre-approved in writing',
              'Comply with Company brand and compliance guidelines',
              'Include valid receipts and proof of expense',
              'Meet all applicable regulatory and RESPA marketing requirements'
            ]
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Unauthorized or non-compliant marketing expenditures will not be reimbursed.'
          }
        ]
      },

      // ====================================================================
      // SECTION 10: OUTSIDE SALES CLASSIFICATION
      // ====================================================================
      {
        id: generateId('section'),
        type: 'outside_sales',
        title: 'Outside Sales Employee Classification',
        order: 9,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '9. Outside Sales Employee Classification',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'FLSA Classification',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer is classified as an "outside sales employee" under the Fair Labor Standards Act (FLSA), 29 C.F.R. § 541.500, and is therefore exempt from minimum wage, overtime, and hourly time-tracking requirements.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Primary Job Duties and Work Location',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer\'s primary duty is to originate mortgage loans and solicit business outside of the Company\'s principal place of business, including meeting with prospective borrowers, referral partners, and real estate professionals.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Compensation is commission-based and earned solely on closed and funded loan transactions, not on hours worked.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Responsibility to Maintain Qualification for Exemption',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer acknowledges and agrees to perform their duties in a manner consistent with the outside sales exemption and to conduct sales-related activities primarily outside the Company\'s physical office locations. Failure to meet the requirements of this classification may result in compensation structure review or termination.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'No Hourly Work Requirement',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer is not required to work a set schedule or maintain time records, and is responsible for managing their schedule and activities in a manner that supports production and compliance obligations.'
          }
        ]
      },

      // ====================================================================
      // SECTION 11: CONFIDENTIALITY
      // ====================================================================
      {
        id: generateId('section'),
        type: 'confidentiality',
        title: 'Confidentiality & Data Protection',
        order: 10,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '10. Confidentiality & Data Protection',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'GLBA & Privacy Compliance',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Loan Officer shall comply with the Gramm-Leach-Bliley Act (GLBA), all related federal and state privacy laws (including CCPA where applicable), and Company privacy and security policies regarding the safeguarding of non-public personal information ("NPI"). Unauthorized access, use, or disclosure of borrower information may result in immediate termination, reporting to regulatory authorities, and legal action.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Data Security Requirements',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Loan Officer shall:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'Use secure, password-protected and Company-approved devices and systems',
              'Encrypt confidential documents and prohibit local saving to unapproved devices',
              'Never transmit NPI through unsecured email, text message, or personal systems',
              'Not store borrower files outside Company platforms, systems, or networks',
              'Immediately report any potential or actual data breach to the Company\'s compliance department'
            ]
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Use of personal email, personal cloud storage, messaging apps, or external systems for borrower or Company information is strictly prohibited unless expressly authorized in writing.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Return of Company Property & Data',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Upon request or termination of this Agreement, Loan Officer must immediately, and no later than forty-eight (48) hours:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'Return all Company property, devices, passwords, records, loan files, and confidential information',
              'Permanently delete and certify deletion of any Company or borrower data stored on personal devices or systems',
              'Cease access to and use of all Company systems, platforms, and communication tools'
            ]
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Survival',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'All confidentiality, privacy, and data protection obligations survive termination of this Agreement indefinitely.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Unauthorized retention, removal, copying, downloading, or use of borrower or Company information after separation constitutes misappropriation of trade secrets and may result in injunctive relief, damages, and attorney fees.'
          }
        ]
      },

      // ====================================================================
      // SECTION 12: DISPUTE RESOLUTION
      // ====================================================================
      {
        id: generateId('section'),
        type: 'dispute_resolution',
        title: 'Dispute Resolution',
        order: 11,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '11. Dispute Resolution',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Binding Arbitration',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Any dispute, claim, or controversy arising out of or relating to this Agreement, the Loan Officer\'s relationship with the Company, or the termination of such relationship, shall be resolved by final and binding arbitration pursuant to the California Arbitration Act (Cal. Code Civ. Proc. §1280 et seq.). Arbitration shall be the exclusive forum for resolving such disputes, and the parties knowingly waive their right to a court or jury trial.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Venue & Rules',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Arbitration shall be conducted in Los Angeles County, California before a single neutral arbitrator mutually agreed upon by the parties, or, if the parties cannot agree, appointed pursuant to the California Arbitration Act. The arbitration shall be administered in accordance with the rules of the selected arbitration provider, unless otherwise required by law.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Attorney Fees and Costs',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Each party shall bear their own attorneys\' fees and costs unless the arbitrator determines that a statute or applicable law requires otherwise or expressly awards fees to the prevailing party.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Governing Law',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'This Agreement and any dispute arising under it shall be governed by and construed in accordance with the laws of the State of California, without regard to conflict-of-law principles.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Severability of Arbitration Provision',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'If any portion of this arbitration provision is found unenforceable, the remaining terms shall remain in full force and effect to the maximum extent permitted by law.'
          }
        ]
      },

      // ====================================================================
      // SECTION 13: PIPELINE OWNERSHIP
      // ====================================================================
      {
        id: generateId('section'),
        type: 'pipeline_ownership',
        title: 'Pipeline & Lead Ownership',
        order: 12,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '12. Pipeline & Lead Ownership',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Company-Generated Leads',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'All leads and prospects originating from Company marketing, advertising, paid or organic lead generation programs, Company website or digital assets, referral relationships established by the Company, or Company-provided resources are the exclusive property of Lendwise Mortgage.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Company reserves the right to reassign such leads and borrower files at any time, including upon the Loan Officer\'s departure or termination.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Self-Generated Leads',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Leads and borrowers sourced solely through the Loan Officer\'s own personal marketing efforts, personal referral relationships, and independent prospecting activities, without the use of Company funds, resources, or lead systems, are considered self-generated leads.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer shall retain the right to commissions for self-generated leads that close and fund within sixty (60) days of termination, provided:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'The loan file is in good standing, and',
              'The Loan Officer cooperates in transferring the file and borrower communications to Company staff upon request.'
            ]
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Company Database, CRM & Borrower Information',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'All borrower data, CRM records, contact lists, pipeline information, loan files, pricing data, underwriting notes, and related business information stored or accessed through Company systems are confidential Company property.'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'The Loan Officer shall not download, copy, remove, transfer, solicit, or otherwise retain any Company or borrower data upon termination. Any attempt to do so shall constitute misappropriation of Company trade secrets and may result in:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'Immediate forfeiture of any pending pipeline commissions',
              'Legal action and injunctive relief',
              'Monetary damages and attorney fees as allowed by law'
            ]
          }
        ]
      },

      // ====================================================================
      // SECTION 14: INDEPENDENT CONTRACTOR STATUS
      // ====================================================================
      {
        id: generateId('section'),
        type: 'independent_contractor',
        title: 'Employment / Independent Contractor Status',
        order: 13,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '13. Employment / Independent Contractor Status',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Classification Determination',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Employment classification (W-2 employee or 1099 independent contractor) shall be determined by the Company in accordance with applicable federal, state, and local laws and regulations, and based on the nature of the Loan Officer\'s work, degree of control, and applicable licensing requirements. The Loan Officer\'s classification shall be confirmed in writing at the time of onboarding and may be adjusted if required by law.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'W-2 Employee Status (If Applicable)',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'If classified as a W-2 employee:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'The Loan Officer will receive a Form W-2 for tax reporting',
              'The Company will withhold required federal, state, and payroll taxes (including Social Security and Medicare)',
              'The Loan Officer may be eligible for Company-provided benefits, if any, subject to applicable plan terms and eligibility rules'
            ]
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: '1099 Independent Contractor Status (If Applicable)',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'If classified as a 1099 independent contractor:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'The Loan Officer will receive a Form 1099-NEC for tax reporting',
              'No federal, state, or payroll taxes will be withheld by the Company',
              'The Loan Officer is solely responsible for payment of all federal and state income taxes, self-employment taxes, and estimated quarterly tax payments',
              'Independent contractors are responsible for managing their own business operations and regulatory licensing responsibilities and are not eligible for Company employee benefits.'
            ]
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Business & Licensing Expenses',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Regardless of classification, the Loan Officer is responsible for maintaining valid and active professional licensing and meeting all associated requirements, including but not limited to:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'NMLS licensing and state regulatory licenses (e.g., DRE)',
              'Continuing education and renewal fees',
              'Personal marketing and business expenses'
            ]
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Failure to maintain required licensing and compliance standards may result in suspension of origination authority and/or termination of this Agreement.'
          }
        ]
      },

      // ====================================================================
      // SECTION 15: LEGAL PROVISIONS
      // ====================================================================
      {
        id: generateId('section'),
        type: 'legal_provisions',
        title: 'Legal Provisions',
        order: 14,
        isEditable: true,
        hasDynamicData: false,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '14. Legal Provisions',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Severability',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'If any provision of this Agreement is determined to be invalid, illegal, or unenforceable, such provision shall be enforced to the maximum extent permitted by law. The remaining provisions shall continue in full force and effect and shall not be affected or impaired.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Entire Agreement',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'This Agreement constitutes the entire understanding between LendWise Mortgage and the Loan Officer with respect to the subject matter herein, and supersedes all prior and contemporaneous agreements, representations, discussions, or understandings, whether written or oral, relating to compensation, employment terms, or loan production arrangements.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Amendments',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'This Agreement may only be amended, modified, or supplemented by a written agreement signed by both parties. No verbal modification, email correspondence, or course of conduct shall be deemed to alter or modify this Agreement unless expressly agreed to in writing by the parties.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'No Waiver',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Failure by the Company to enforce any term or provision of this Agreement shall not constitute a waiver and shall not affect the right to enforce such term or any other provision at any time thereafter.'
          }
        ]
      },

      // ====================================================================
      // SECTION 16: SIGNATURE BLOCK
      // ====================================================================
      {
        id: generateId('section'),
        type: 'signature_block',
        title: 'Signatures',
        order: 15,
        isEditable: true,
        hasDynamicData: true,
        content: [
          {
            id: generateId('content'),
            type: 'heading',
            text: '15. Signatures',
            level: 2
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'IN WITNESS WHEREOF, the parties have executed this Loan Officer Compensation Agreement as of the date first written above.'
          },
          {
            id: generateId('content'),
            type: 'divider'
          },
          {
            id: generateId('content'),
            type: 'signature_field',
            label: 'Loan Officer Signature',
            signatureVariable: 'fullName',
            dateVariable: 'contractDate'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Loan Officer Name: {{fullName}}'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Date: {{contractDate:date}}'
          },
          {
            id: generateId('content'),
            type: 'divider'
          },
          {
            id: generateId('content'),
            type: 'signature_field',
            label: 'Company Representative Signature',
            signatureVariable: 'companyName'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Company Name: {{companyName}}'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Representative Name: _____________________________'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Title: _____________________________'
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Date: _____________________________'
          }
        ]
      }
    ]
  };
}
